import { useState, useEffect } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import service from '@Service';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@Utils/cn';
import { Edit, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const CategorySchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, 'Please enter at least 2 characters'),
  description: z
    .string()
    .max(500, 'Keep it under 500 characters')
    .optional()
    .or(z.literal('')),
});

export type UserRoleFormValues = z.infer<typeof CategorySchema>;

function UserRoleForm({
  edit = false,
  data = null,
}: {
  edit?: boolean;
  data?: UserRoleFormValues | null;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const query = useQueryClient();

  const form = useForm<UserRoleFormValues>({
    resolver: zodResolver(CategorySchema),
    defaultValues: {
      id: data?.id || '',
      name: data?.name || '',
      description: data?.description || '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { handleSubmit, control, reset } = form;

  const onSubmit = async (values: UserRoleFormValues) => {
    const { name, description, id } = values;
    if (edit) {
      const updatedRole = await service.updateUserRole(id, {
        name,
        description,
      });

      if (!updatedRole) {
        toastError('Error updating user role');
      } else {
        query.invalidateQueries({ queryKey: ['userRoles'] });
        setSheetOpen(false);
        toastSuccess('User role updated successfully');
      }
    } else {
      const newRole = await service.createUserRole(name, description);
      if (!newRole) {
        toastError('Error creating user role');
      } else {
        query.invalidateQueries({ queryKey: ['userRoles'] });
        setSheetOpen(false);
        toastSuccess('User role created successfully');
      }
    }
  };

  useEffect(() => {
    if (data) {
      reset({
        id: data?.id,
        name: data?.name,
        description: data?.description,
      });
    } else {
      reset({ id: '', name: '', description: '' });
    }
  }, [data, sheetOpen, reset]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            icon={{
              src: edit ? <Edit /> : <Plus />,
            }}
            className={cn('justify-start')}
          >
            {edit ? 'Edit' : 'Create new'} user role
          </Button>
        </SheetTrigger>
        <SheetContent className="gap-0">
          <Form {...form}>
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleSubmit(onSubmit)}
            >
              <SheetHeader>
                <SheetTitle>{edit ? 'Edit' : 'Create'} User Role</SheetTitle>
                <SheetDescription>{edit ? data?.name : ''}</SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="max-w-sm space-y-2">
                  <FormField
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input type="text" {...field} placeholder="Name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="Short description..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>
              <SheetFooter>
                <Button type="submit">{edit ? 'Save' : 'Submit'}</Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default UserRoleForm;
